import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, siteInfo } from '@/mocks/homeData';

const avatarImages: Record<string, string> = {
  'avatar-1': 'https://readdy.ai/api/search-image?query=Happy%20Chinese%20couple%20portrait%20on%20tropical%20beach%2C%20warm%20smiling%20faces%2C%20natural%20golden%20hour%20lighting%2C%20casual%20vacation%20outfits%2C%20professional%20portrait%20photography%20with%20soft%20background%20blur&width=120&height=120&seq=avatar-testimonial-1&orientation=squarish',
  'avatar-2': 'https://readdy.ai/api/search-image?query=Romantic%20Chinese%20couple%20close%20up%20portrait%20at%20sunset%2C%20tender%20expression%2C%20engaged%20couple%20showing%20ring%2C%20warm%20golden%20light%2C%20professional%20couple%20photography%20with%20shallow%20depth%20of%20field&width=120&height=120&seq=avatar-testimonial-2&orientation=squarish',
  'avatar-3': 'https://readdy.ai/api/search-image?query=Young%20Chinese%20couple%20smiling%20portrait%20outdoor%2C%20casual%20and%20natural%20style%2C%20bright%20sunny%20day%2C%20tropical%20background%20with%20palm%20trees%2C%20professional%20lifestyle%20portrait%20photography&width=120&height=120&seq=avatar-testimonial-3&orientation=squarish',
};

export default function AboutPage() {
  const [showFormSuccess, setShowFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeyValue = (formData.get('website_alt') as string || '').trim();
    if (honeyValue) {
      setShowFormSuccess(true);
      setFormError('');
      form.reset();
      setTimeout(() => setShowFormSuccess(false), 3000);
      return;
    }

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9q14j4nlsngrm4llpq0';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setShowFormSuccess(true);
        setFormError('');
        form.reset();
        setTimeout(() => setShowFormSuccess(false), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormError('网络异常，请检查网络后重试');
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://aiqingdao.com',
    description: siteInfo.description,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '三亚市',
      addressRegion: '海南省',
      addressCountry: 'CN',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、服务理念和客户口碑`,
    url: 'https://aiqingdao.com/about',
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <header><Navbar /></header>

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <div className="container-wide py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              用心规划每一段浪漫旅程，让爱情在最美的海岛绽放
            </p>
          </div>
        </div>

        <section className="section-padding bg-background-50">
          <div className="container-wide">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                  <p>
                    {siteInfo.name}创立于2018年，由一群热爱旅行、相信爱情的海岛生活家共同创建。我们的初心很简单——让每一对相爱的人，都能在最浪漫的海岛上，创造属于他们的独家记忆。
                  </p>
                  <p>
                    五年来，我们走遍了爱情岛及周边海域的每一个角落，从无人知晓的秘境沙滩到最受欢迎的打卡景点，从地道渔家小馆到顶级悬崖餐厅。我们用脚步丈量每一寸土地，只为给你和TA推荐最值得体验的线路。
                  </p>
                  <p>
                    至今，我们已经服务了超过50万对情侣和新婚夫妇，策划了超过3000场海岛求婚仪式，见证了无数个"我愿意"的动人瞬间。每一次看到客户发来的甜蜜反馈，都让我们更加坚定——这份事业值得我们投入全部的热情。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="bg-primary-50 rounded-xl p-4 text-center">
                    <p className="text-2xl md:text-3xl font-bold font-heading text-primary-600">5年+</p>
                    <p className="text-xs text-foreground-500 mt-1">行业经验</p>
                  </div>
                  <div className="bg-accent-50 rounded-xl p-4 text-center">
                    <p className="text-2xl md:text-3xl font-bold font-heading text-accent-600">50万+</p>
                    <p className="text-xs text-foreground-500 mt-1">服务旅客</p>
                  </div>
                  <div className="bg-secondary-50 rounded-xl p-4 text-center">
                    <p className="text-2xl md:text-3xl font-bold font-heading text-secondary-600">3000+</p>
                    <p className="text-xs text-foreground-500 mt-1">求婚策划</p>
                  </div>
                </div>
              </div>

              <div className="flex-1 w-full">
                <div
                  className="w-full h-72 md:h-80 lg:h-96 rounded-xl bg-cover bg-center"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20travel%20team%20standing%20together%20on%20a%20beautiful%20tropical%20beach%20at%20sunset%2C%20diverse%20friendly%20group%20wearing%20casual%20resort%20uniforms%2C%20warm%20smiling%20faces%2C%20palm%20trees%20and%20ocean%20in%20background%2C%20golden%20hour%20lighting%2C%20team%20portrait%20photography%20style%2C%20professional%20and%20welcoming%20atmosphere&width=1000&height=750&seq=about-brand-story&orientation=landscape')`,
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-100">
          <div className="container-wide">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                客户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                来自真实客户的甜蜜反馈，是我们最大的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-full bg-cover bg-center flex-shrink-0"
                      style={{ backgroundImage: `url('${avatarImages[t.avatarSeq]}')` }}
                    />
                    <div>
                      <p className="text-sm font-bold font-heading text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.trip}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-star-fill text-xs text-accent-500"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-50">
          <div className="container-wide">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mb-6">
                  有任何疑问或需要定制专属行程？我们随时为你服务
                </p>

                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-base"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电话咨询</p>
                      <a href="tel:4008886688" className="text-sm text-primary-600 hover:text-primary-700">{siteInfo.phone}</a>
                      <p className="text-xs text-foreground-500 mt-0.5">服务时间：每日 8:00 - 22:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-base"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">邮件联系</p>
                      <a href={`mailto:${siteInfo.email}`} className="text-sm text-primary-600 hover:text-primary-700">{siteInfo.email}</a>
                      <p className="text-xs text-foreground-500 mt-0.5">我们会在24小时内回复</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-base"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-background-100 rounded-xl p-6 md:p-8">
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">在线留言</h3>

                  {showFormSuccess && (
                    <div className="mb-4 p-3 bg-secondary-50 text-secondary-700 rounded-lg text-sm">
                      感谢您的留言！我们会在24小时内与您联系。
                    </div>
                  )}

                  {formError && (
                    <div className="mb-4 p-3 bg-primary-50 text-primary-700 rounded-lg text-sm">
                      {formError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                    <input
                      type="text"
                      name="website_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1">电话</label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的手机号"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱（选填）"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请描述您的需求，如出行人数、时间、预算等（500字以内）"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}